import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  if (!currentCategory) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 rounded-full bg-accent-100">
              <i className="ri-emotion-sad-line text-3xl text-accent-600"></i>
            </div>
            <h2 className="text-2xl font-bold font-heading text-foreground-950 mb-3">
              分类未找到
            </h2>
            <p className="text-foreground-600 mb-6">抱歉，您访问的分类页面不存在。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />

            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100">
                    <i className={`${currentCategory.icon} text-xl text-accent-600`}></i>
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                      {currentCategory.name}
                    </h1>
                    <p className="text-sm text-foreground-600 mt-1">
                      共 {categoryArticles.length} 篇文章
                    </p>
                  </div>
                </div>

                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                  {currentCategory.description}
                </p>

                {categoryArticles.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                    {categoryArticles.map((article) => (
                      <ArticleCard key={article.slug} article={article} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-background-50 rounded-lg border border-background-200/70">
                    <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-100">
                      <i className="ri-file-list-3-line text-2xl text-secondary-600"></i>
                    </div>
                    <p className="text-foreground-600 text-sm">
                      该分类下暂无文章，敬请期待。
                    </p>
                  </div>
                )}
              </div>

              <aside className="lg:w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <h3 className="text-sm font-semibold font-heading text-foreground-950 mb-4">
                    文章分类
                  </h3>
                  <nav className="space-y-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm cursor-pointer transition-colors whitespace-nowrap ${
                          cat.slug === slug
                            ? 'bg-primary-100/70 text-primary-700 font-medium'
                            : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-950'
                        }`}
                      >
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100">
                          <i className={`${cat.icon} text-accent-600 text-xs`}></i>
                        </div>
                        {cat.name}
                      </Link>
                    ))}
                  </nav>

                  <div className="mt-8 p-4 bg-accent-100/60 rounded-lg">
                    <h4 className="text-sm font-semibold font-heading text-foreground-950 mb-2">
                      需要帮助？
                    </h4>
                    <p className="text-xs text-foreground-600 leading-relaxed mb-3">
                      如果您在养殖过程中遇到任何问题，欢迎随时联系我们获取专业指导。
                    </p>
                    <Link
                      to="/about"
                      className="inline-flex items-center gap-1 text-xs font-medium text-accent-700 hover:text-accent-800 cursor-pointer transition-colors"
                    >
                      联系我们
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}